/**
 * LJ Bundle Builder
 * Manages bundle state, variant selection (incl. colour + size),
 * image swap on variant change, sidebar updates, celebrations,
 * and Shopify Cart API integration.
 *
 * Reads product data + tier config from a JSON script tag rendered by Liquid.
 */
(function () {
  'use strict';

  // ── Bootstrap from Liquid data ──────────────────────────────────────────
  const dataEl = document.getElementById('lj-bundle-data');
  if (!dataEl) return;
  const DATA = JSON.parse(dataEl.textContent);
  const TIERS = DATA.tiers;
  const PRODUCTS = DATA.products;
  const FREE_SHIP_MIN = DATA.freeShippingMin || 0;

  // ── State ───────────────────────────────────────────────────────────────
  let bundle = {};            // { variantId: quantity }
  let selectedOptions = {};   // { productId: { optionName: value } }

  // Pre-select first available variant's options for every product
  Object.values(PRODUCTS).forEach(p => {
    if (p.variants.length && p.options.length) {
      selectedOptions[p.id] = {};
      const defaultVariant = p.variants.find(v => v.available) || p.variants[0];
      p.options.forEach((opt, i) => {
        selectedOptions[p.id][opt.name] = defaultVariant[`option${i + 1}`];
      });
    }
  });

  // ── Helpers ─────────────────────────────────────────────────────────────
  function fmt(pence) {
    return `£${(pence / 100).toFixed(2)}`;
  }

  function findVariant(product, opts) {
    return product.variants.find(v =>
      product.options.every((opt, i) => v[`option${i + 1}`] === opts[opt.name])
    );
  }

  function currentVariant(productId) {
    const p = PRODUCTS[productId];
    if (!p) return null;
    if (p.variants.length === 1) return p.variants[0];
    const v = findVariant(p, selectedOptions[productId] || {});
    if ((!v || !v.available) && p.options.length) {
      const fa = p.variants.find(av => av.available);
      if (fa) {
        if (!selectedOptions[productId]) selectedOptions[productId] = {};
        p.options.forEach((opt, i) => {
          selectedOptions[productId][opt.name] = fa[`option${i + 1}`];
        });
        return fa;
      }
    }
    return v;
  }

  function findVariantById(vid) {
    for (const p of Object.values(PRODUCTS)) {
      const v = p.variants.find(x => String(x.id) === String(vid));
      if (v) return v;
    }
    return null;
  }

  function findProductByVariantId(vid) {
    for (const p of Object.values(PRODUCTS)) {
      if (p.variants.find(x => String(x.id) === String(vid))) return p;
    }
    return null;
  }

  function getSubtotal() {
    let total = 0;
    for (const [vid, qty] of Object.entries(bundle)) {
      const v = findVariantById(vid);
      if (v) total += v.price * qty;
    }
    return total;
  }

  function itemCount() {
    return Object.values(bundle).reduce((a, b) => a + b, 0);
  }

  function currentTier() {
    const s = getSubtotal();
    for (let i = TIERS.length - 1; i >= 0; i--) {
      if (s >= TIERS[i].threshold) return i;
    }
    return -1;
  }

  function progress() {
    const s = getSubtotal();
    const t0 = TIERS[0].threshold, t1 = TIERS[1].threshold, t2 = TIERS[2].threshold;
    if (s <= 0) return 0;
    if (s >= t2) return 100;
    if (s >= t1) return 50 + ((s - t1) / (t2 - t1)) * 33.33;
    if (s >= t0) return 16.67 + ((s - t0) / (t1 - t0)) * 33.33;
    return (s / t0) * 16.67;
  }

  function savings() {
    const t = currentTier();
    if (t < 0) return 0;
    return Math.round(getSubtotal() * TIERS[t].discount / 100);
  }

  function message() {
    const s = getSubtotal();
    const t = currentTier();
    if (s === 0) return 'Add products to start saving';
    if (t < 0) return `Spend ${fmt(TIERS[0].threshold - s)} more to unlock ${TIERS[0].discount}% off`;
    if (t === 0) return `You're saving ${TIERS[0].discount}% – spend ${fmt(TIERS[1].threshold - s)} more for ${TIERS[1].discount}% off`;
    if (t === 1) return `You're saving ${TIERS[1].discount}% – spend ${fmt(TIERS[2].threshold - s)} more for ${TIERS[2].discount}% off`;
    return `Maximum savings unlocked! You're saving ${TIERS[2].discount}%`;
  }

  // ── Actions ─────────────────────────────────────────────────────────────
  function selectOption(productId, optionName, value) {
    if (!selectedOptions[productId]) selectedOptions[productId] = {};
    selectedOptions[productId][optionName] = value;
    refreshCards(productId);
  }

  function addToBundle(productId) {
    const v = currentVariant(productId);
    if (!v || !v.available) return;
    const vid = String(v.id);
    bundle[vid] = (bundle[vid] || 0) + 1;
    refreshAll();
  }

  function removeFromBundle(vid) {
    delete bundle[vid];
    refreshAll();
  }

  function changeQty(vid, delta) {
    if (!bundle[vid]) return;
    bundle[vid] += delta;
    if (bundle[vid] <= 0) delete bundle[vid];
    refreshAll();
  }

  // ── Card rendering ──────────────────────────────────────────────────────
  function refreshCards(onlyProductId) {
    const cards = onlyProductId
      ? document.querySelectorAll(`.lj-bundle-card[data-product-id="${onlyProductId}"]`)
      : document.querySelectorAll('.lj-bundle-card');

    cards.forEach(card => {
      const pid = card.dataset.productId;
      const p = PRODUCTS[pid];
      if (!p) return;
      const v = currentVariant(pid);
      const vid = v ? String(v.id) : null;
      const inBundle = vid && bundle[vid] > 0;

      // Update option pills with linked variant availability
      card.querySelectorAll('.lj-bundle-card__pill').forEach(pill => {
        const optName = pill.dataset.optionName;
        const val = pill.dataset.value;
        const sel = selectedOptions[pid] && selectedOptions[pid][optName] === val;
        pill.classList.toggle('is-selected', sel);

        // Check if selecting this value leads to an available variant
        const testOpts = Object.assign({}, selectedOptions[pid] || {});
        testOpts[optName] = val;
        const testV = findVariant(p, testOpts);
        pill.classList.toggle('is-unavailable', !!testV && !testV.available);
      });

      // Update product card image based on selected variant
      if (v) {
        const imgSrc = v.image || p.featuredImage;
        if (imgSrc) {
          const img = card.querySelector('.lj-bundle-card__image-wrap img');
          if (img && img.src !== imgSrc) {
            img.src = imgSrc;
            if (img.srcset) img.srcset = '';
          }
        }
      }

      // Update price display
      if (v) {
        const priceEl = card.querySelector('.lj-bundle-card__price-current');
        if (priceEl) priceEl.textContent = fmt(v.price);
      }

      // Update add button / qty controls
      const actionsEl = card.querySelector('.lj-bundle-card__actions');
      if (!actionsEl) return;

      if (inBundle) {
        actionsEl.innerHTML =
          `<div class="lj-bundle-card__qty">` +
            `<button class="lj-bundle-card__qty-btn" data-action="qty" data-vid="${vid}" data-delta="-1">−</button>` +
            `<div class="lj-bundle-card__qty-val">${bundle[vid]}</div>` +
            `<button class="lj-bundle-card__qty-btn" data-action="qty" data-vid="${vid}" data-delta="1">+</button>` +
          `</div>`;
      } else {
        const disabled = !v || !v.available;
        const label = disabled ? 'Sold out' : 'Add to bundle';
        actionsEl.innerHTML = `<button class="lj-bundle-card__add product-card--add-to-cart-button outline button accent" data-action="add" data-product-id="${pid}"${disabled ? ' disabled' : ''}><span class="product-card--add-to-cart-text">${label}</span></button>`;
      }
    });
  }

  // ── Sidebar rendering ───────────────────────────────────────────────────
  function refreshSidebar() {
    const count = itemCount();
    const sub = getSubtotal();
    const tier = currentTier();
    const prog = progress();
    const sav = savings();

    // Count badge
    const countEl = document.getElementById('lj-sb-count');
    if (countEl) {
      countEl.textContent = count;
      countEl.classList.add('is-bounce');
      setTimeout(() => countEl.classList.remove('is-bounce'), 300);
    }

    // Message
    const msgEl = document.getElementById('lj-sb-message');
    if (msgEl) msgEl.textContent = message();

    // Progress fill
    const fillEl = document.getElementById('lj-sb-fill');
    if (fillEl) fillEl.style.width = `${prog}%`;

    // Dots + labels
    for (let i = 0; i < 3; i++) {
      const dot = document.getElementById(`lj-sb-dot-${i}`);
      const lbl = document.getElementById(`lj-sb-lbl-${i}`);
      if (dot) dot.classList.toggle('on', tier >= i);
      if (lbl) lbl.classList.toggle('on', tier >= i);
    }

    // Items list
    const body = document.getElementById('lj-sb-items');
    if (!body) return;
    if (count === 0) {
      body.innerHTML = '<div class="lj-bundle-sidebar__empty"><div class="lj-bundle-sidebar__empty-icon">+</div><div class="lj-bundle-sidebar__empty-text">Add products to start</div></div>';
    } else {
      body.innerHTML = Object.entries(bundle).map(([vid, qty]) => {
        const p = findProductByVariantId(vid);
        const v = findVariantById(vid);
        if (!p || !v) return '';
        const varLabel = p.variants.length > 1 ? v.title.replace(/ \/ /g, ' · ') : '';
        const imgSrc = v.image || p.featuredImage || '';
        const imgHtml = imgSrc
          ? `<img class="lj-bundle-sidebar__item-img" src="${imgSrc}" alt="${p.title}">`
          : `<div class="lj-bundle-sidebar__item-img" style="display:flex;align-items:center;justify-content:center;font-weight:700;color:var(--lj-grey);font-size:0.875rem;">${p.title.charAt(0)}</div>`;
        return `
          <div class="lj-bundle-sidebar__item">
            ${imgHtml}
            <div class="lj-bundle-sidebar__item-info">
              <div class="lj-bundle-sidebar__item-name">${p.title}</div>
              ${varLabel ? `<div class="lj-bundle-sidebar__item-variant">${varLabel}</div>` : ''}
              <div class="lj-bundle-sidebar__item-price">${fmt(v.price)} × ${qty}</div>
            </div>
            <div class="lj-bundle-sidebar__item-qty">
              <button class="lj-bundle-sidebar__item-qty-btn" data-action="qty" data-vid="${vid}" data-delta="-1">−</button>
              <span class="lj-bundle-sidebar__item-qty-num">${qty}</span>
              <button class="lj-bundle-sidebar__item-qty-btn" data-action="qty" data-vid="${vid}" data-delta="1">+</button>
            </div>
            <button class="lj-bundle-sidebar__item-remove" data-action="remove" data-vid="${vid}">×</button>
          </div>`;
      }).join('');
    }

    // Totals
    const discountedSub = sub - sav;
    const shippingCost  = (count > 0 && discountedSub >= FREE_SHIP_MIN) ? 0 : 399;
    const totalsEl = document.getElementById('lj-sb-totals');
    if (totalsEl) totalsEl.style.display = count > 0 ? 'block' : 'none';
    const hasSavings = tier >= 0 && sav > 0;
    const fullPriceRow = document.getElementById('lj-sb-fullprice-row');
    const fullPriceEl  = document.getElementById('lj-sb-fullprice');
    const savingsRow   = document.getElementById('lj-sb-savings-row');
    const savLabel     = document.getElementById('lj-sb-sav-label');
    const savVal       = document.getElementById('lj-sb-sav-value');
    const subtotalRow  = document.getElementById('lj-sb-subtotal-row');
    const subEl        = document.getElementById('lj-sb-subtotal');
    const totalEl      = document.getElementById('lj-sb-total');
    const shippingRow  = document.getElementById('lj-sb-shipping-row');
    const shippingVal  = document.getElementById('lj-sb-shipping-val');
    if (fullPriceRow) fullPriceRow.style.display = hasSavings ? 'flex' : 'none';
    if (fullPriceEl)  fullPriceEl.textContent  = fmt(sub);
    if (savingsRow)   savingsRow.style.display  = hasSavings ? 'flex' : 'none';
    if (savLabel && tier >= 0) savLabel.textContent = `You saved ${TIERS[tier].discount}%`;
    if (savVal)       savVal.textContent = `-${fmt(sav)}`;
    if (subtotalRow)  subtotalRow.style.display = hasSavings ? 'none' : 'flex';
    if (subEl)        subEl.textContent  = fmt(sub);
    if (shippingRow)  shippingRow.style.display = count > 0 ? 'flex' : 'none';
    if (shippingVal) {
      if (count > 0 && discountedSub >= FREE_SHIP_MIN) {
        shippingVal.textContent = 'FREE';
        shippingVal.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;width:74px;font-size:11px;font-weight:700;color:#ffffff;background:#48d597;border-radius:50px;padding:3px 0;letter-spacing:0.04em;text-transform:uppercase;box-sizing:border-box;';
      } else {
        shippingVal.textContent = '£3.99';
        shippingVal.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;width:74px;font-size:13px;color:#28292a;';
      }
    }
    if (totalEl) totalEl.textContent = fmt(discountedSub + shippingCost);

    // CTA
    const cta = document.getElementById('lj-sb-cta');
    if (cta) {
      cta.className = 'lj-bundle-sidebar__cta product-card--add-to-cart-button outline button accent';
      if (count === 0) {
        cta.textContent = 'Add products to start';
        cta.disabled = true;
      } else if (tier === 2) {
        cta.classList.add('is-max');
        cta.textContent = `Add bundle to cart – ${fmt(discountedSub + shippingCost)}`;
        cta.disabled = false;
      } else if (tier >= 0) {
        cta.classList.add('is-tier');
        cta.textContent = `Add bundle to cart – ${fmt(discountedSub + shippingCost)}`;
        cta.disabled = false;
      } else {
        cta.textContent = `Add bundle to cart – ${fmt(sub + shippingCost)}`;
        cta.disabled = false;
      }
    }
  }

  // ── Add to Cart (Shopify Cart API) ──────────────────────────────────────
  function addBundleToCart() {
    const items = Object.entries(bundle).map(([vid, qty]) => ({
      id: parseInt(vid),
      quantity: qty
    }));
    if (!items.length) return;

    const cta = document.getElementById('lj-sb-cta');
    if (cta) { cta.textContent = 'Adding…'; cta.disabled = true; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    })
      .then(r => { if (!r.ok) throw new Error('Cart error'); return r.json(); })
      .then(() => {
        bundle = {};
        sessionStorage.removeItem('lj_bundle');
        refreshAll();
        // Open cart drawer — Fuel Themes Reformation
        const cartBtn = document.querySelector('.header-cart__icon, [data-cart-toggle], .js-cart-drawer-toggle');
        if (cartBtn) cartBtn.click();
        else window.location.href = '/cart';
      })
      .catch(err => {
        console.error('Bundle add to cart failed:', err);
        if (cta) { cta.textContent = 'Error – try again'; cta.disabled = false; }
      });
  }

  // ── Refresh all ─────────────────────────────────────────────────────────
  function refreshAll() {
    refreshCards();
    refreshSidebar();
    sessionStorage.setItem('lj_bundle', JSON.stringify(bundle));
  }

  // ── Event delegation (single handler) ──────────────────────────────────
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    const action = btn.dataset.action;

    if (action === 'add') {
      addToBundle(btn.dataset.productId);
    } else if (action === 'qty') {
      changeQty(btn.dataset.vid, parseInt(btn.dataset.delta));
    } else if (action === 'remove') {
      removeFromBundle(btn.dataset.vid);
    } else if (action === 'add-to-cart') {
      if (!btn.disabled) addBundleToCart();
    } else if (action === 'toggle-sidebar') {
      const wrap = document.querySelector('.lj-bundle-sidebar-wrap');
      if (wrap) wrap.classList.toggle('is-expanded');
    } else if (action === 'select-option') {
      if (btn.classList.contains('is-unavailable')) return;
      selectOption(btn.dataset.productId, btn.dataset.optionName, btn.dataset.value);
    }
  });

  // ── Init ────────────────────────────────────────────────────────────────
  const _saved = sessionStorage.getItem('lj_bundle');
  if (_saved) {
    try {
      Object.entries(JSON.parse(_saved)).forEach(([vid, qty]) => {
        if (findVariantById(vid)) bundle[vid] = qty;
      });
    } catch(e) {}
  }

  const _params = new URLSearchParams(window.location.search);
  const _preAdd = _params.get('add');
  if (_preAdd && findVariantById(_preAdd)) bundle[String(_preAdd)] = (bundle[String(_preAdd)] || 0) + 1;

  refreshAll();

})();
